import type { MetricsFrameworkTabId, PageConfig } from '../types';
import { getRoutes } from 'utils/auth';

export const METRICS_FRAMEWORK_VIEWS = [
  'overview',
  'your-dealership',
  'revival-results',
  'activity',
  'pipeline',
  'looking-ahead',
] as const;

export type MetricsFrameworkViewMeta = {
  id: MetricsFrameworkTabId;
  /** Tab label in UI */
  tabLabel: string;
  breadcrumbLabel: string;
  /**
   * Admin resource name / page — same shape as UserManagement/Users.
   * Example: ImpactHub/Overview
   */
  resourceName: string;
  /** Exact resource path — same shape as /user-management?view=users */
  path: string;
};

/** Canonical Impact Hub URL for both blue and NewAIAvatarLayout themes. */
export const IMPACT_HUB_BASE_PATH = '/impact-hub';
export const BLUE_LAYOUT_BASE_PATH = IMPACT_HUB_BASE_PATH;
export const AI_AVATAR_BASE_PATH = IMPACT_HUB_BASE_PATH;

/** Legacy path kept only for redirects / resource_mappings compat. */
export const LEGACY_AI_AVATAR_IMPACT_HUB_PATH = '/ai-avatar-layout/impact-hub';

/**
 * Resource name for Use Mock API toggle (`hasResource` / resource_mappings).
 * Grant via admin resource named exactly `ImpactHubUseMockButton`.
 */
export const IMPACT_HUB_USE_MOCK_BUTTON_RESOURCE = 'ImpactHubUseMockButton';

/**
 * Resource name for metric/insight info icon (`hasResource` / resource_mappings).
 * Grant via admin resource named exactly `MetricInfo`.
 */
export const METRIC_INFO_RESOURCE = 'MetricInfo';

/**
 * Resource name for bottom-right catalog id on tiles (`hasResource` / resource_mappings).
 * Grant via admin resource named exactly `CatalogId`.
 */
export const CATALOG_ID_RESOURCE = 'CatalogId';

const toResourceSegment = (view: MetricsFrameworkTabId): string =>
  view
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

export const buildMetricsFrameworkPath = (
  basePath: string,
  view: MetricsFrameworkTabId,
): string => `${basePath}?view=${view}`;

export const METRICS_FRAMEWORK_VIEW_CONFIG: Record<
  MetricsFrameworkTabId,
  MetricsFrameworkViewMeta
> = {
  overview: {
    id: 'overview',
    tabLabel: 'Overview',
    breadcrumbLabel: 'Overview',
    resourceName: 'ImpactHub/Overview',
    path: buildMetricsFrameworkPath(IMPACT_HUB_BASE_PATH, 'overview'),
  },
  'your-dealership': {
    id: 'your-dealership',
    tabLabel: 'Your dealership',
    breadcrumbLabel: 'Your dealership',
    resourceName: 'ImpactHub/YourDealership',
    path: buildMetricsFrameworkPath(IMPACT_HUB_BASE_PATH, 'your-dealership'),
  },
  'revival-results': {
    id: 'revival-results',
    tabLabel: 'Revival results',
    breadcrumbLabel: 'Revival results',
    resourceName: 'ImpactHub/RevivalResults',
    path: buildMetricsFrameworkPath(IMPACT_HUB_BASE_PATH, 'revival-results'),
  },
  activity: {
    id: 'activity',
    tabLabel: 'Activity',
    breadcrumbLabel: 'Activity',
    resourceName: 'ImpactHub/Activity',
    path: buildMetricsFrameworkPath(IMPACT_HUB_BASE_PATH, 'activity'),
  },
  pipeline: {
    id: 'pipeline',
    tabLabel: 'Pipeline',
    breadcrumbLabel: 'Pipeline',
    resourceName: 'ImpactHub/Pipeline',
    path: buildMetricsFrameworkPath(IMPACT_HUB_BASE_PATH, 'pipeline'),
  },
  'looking-ahead': {
    id: 'looking-ahead',
    tabLabel: 'Looking ahead',
    breadcrumbLabel: 'Looking ahead',
    resourceName: 'ImpactHub/LookingAhead',
    path: buildMetricsFrameworkPath(IMPACT_HUB_BASE_PATH, 'looking-ahead'),
  },
};

export const isMetricsFrameworkTabId = (
  value: string | null,
): value is MetricsFrameworkTabId =>
  !!value && (METRICS_FRAMEWORK_VIEWS as readonly string[]).includes(value);

export const getViewFromSearch = (search: string): string | null => {
  try {
    const params = new URLSearchParams(search);
    const view = params.get('view');
    return view ? view.trim() : null;
  } catch {
    return null;
  }
};

export const resolveMetricsFrameworkBasePath = (_pathname?: string): string =>
  IMPACT_HUB_BASE_PATH;

export const isImpactHubPath = (pathname?: string): boolean => {
  if (!pathname) return false;
  const path = pathname.split('?')[0].split('#')[0];
  return path === IMPACT_HUB_BASE_PATH || path.startsWith(`${IMPACT_HUB_BASE_PATH}/`);
};

/** Normalize legacy AI-avatar Impact Hub paths onto the shared `/impact-hub` URL. */
export const normalizeImpactHubResourcePath = (value: string): string =>
  (value || '')
    .trim()
    .replace(/^\/ai-avatar-layout\/impact-hub(?=\?|$)/, IMPACT_HUB_BASE_PATH);

export type MetricsFrameworkBreadcrumb = {
  label: string;
  link?: string;
  isActive?: boolean;
};

export const buildMetricsFrameworkBreadcrumbs = ({
  dealerTitle,
  basePath,
  selectedView,
}: {
  dealerTitle: string | null;
  basePath: string;
  selectedView: MetricsFrameworkTabId | null;
}): MetricsFrameworkBreadcrumb[] => {
  const crumbs: MetricsFrameworkBreadcrumb[] = [];
  if (dealerTitle) {
    crumbs.push({ label: dealerTitle });
  }
  crumbs.push({
    label: 'Impact Hub',
    link: selectedView ? buildMetricsFrameworkPath(basePath, 'overview') : undefined,
    isActive: !selectedView,
  });
  if (selectedView) {
    crumbs.push({
      label: METRICS_FRAMEWORK_VIEW_CONFIG[selectedView].breadcrumbLabel,
      link: buildMetricsFrameworkPath(basePath, selectedView),
      isActive: true,
    });
  }
  return crumbs;
};

/**
 * Same pattern as UserManagement: only tabs whose exact resource path
 * (e.g. `/impact-hub?view=pipeline`) exists in resource_mappings are shown.
 * Also accepts `ImpactHub/<Tab>` resource name if present.
 */
export const getAllowedMetricsFrameworkViews = (
  _basePath: string = IMPACT_HUB_BASE_PATH,
): MetricsFrameworkTabId[] => {
  try {
    const routes = getRoutes() || [];
    return METRICS_FRAMEWORK_VIEWS.filter(view => {
      const { path, resourceName } = METRICS_FRAMEWORK_VIEW_CONFIG[view];
      return routes.some(route => {
        const routePath = normalizeImpactHubResourcePath(route?.path ?? '');
        return (
          routePath === path ||
          route?.name === resourceName ||
          route?.page === resourceName ||
          route?.component === resourceName
        );
      });
    });
  } catch (error) {
    console.error('Failed to resolve allowed Impact Hub views:', error);
    return [];
  }
};

/** @deprecated Prefer METRICS_FRAMEWORK_VIEW_CONFIG[view].resourceName */
export const getImpactHubResourceName = (view: MetricsFrameworkTabId): string =>
  METRICS_FRAMEWORK_VIEW_CONFIG[view].resourceName ?? `ImpactHub/${toResourceSegment(view)}`;

export type { PageConfig };
